/**
 * One CSV cell, RFC-4180.
 *
 * Quoted only when the value needs it, so a number stays a number in a spreadsheet. The
 * previous writer quoted every cell and escaped an inner quote as \" — not the RFC's escape,
 * which is a doubled "" — so any address or note containing a quote arrived with a literal
 * backslash in it and split its row one field early in Excel.
 *
 * A 14-17 digit device serial is the exception. Excel reads a bare IMEI as a number and shows
 * 8.60896E+14, losing the trailing digits the moment the file is saved. Quoting alone does not
 * stop that; the leading = forces a text cell.
 */
const csvCell = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const text = String(value);
    if (/^\d{14,17}$/.test(text.trim())) return `="${text}"`;
    return /["\n,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Header row from the first object's keys, then every row read in that same key order. */
const convertArrayOfObjectsToCSV = (array) => {
    const keys = Object.keys(array[0]);
    return [keys, ...array.map((item) => keys.map((key) => item[key]))]
        .map((row) => row.map(csvCell).join(','))
        .join('\n');
};

/**
 * Download `array` as a CSV file.
 *
 * A Blob rather than a data: URI. encodeURI leaves # and ? intact, so an export carrying either
 * — a street name, a note — was truncated at that character by the browser, and a data: URI is
 * capped well below the size of a month of trips for several assets.
 */
export const downloadCSV = (array, fileName) => {
    if (!Array.isArray(array) || array.length === 0) return;

    const blob = new Blob([`${convertArrayOfObjectsToCSV(array)}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    // In the document, and revoked a tick after the click: Safari and Firefox abort a detached
    // anchor's download, and revoking in the same tick races the browser's read of the blob —
    // which bites hardest on the large exports this rewrite exists to enable.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};
